'use client';

import { LAYER_LABEL } from '@slims/shared/energy';
import {
	Area,
	CartesianGrid,
	ComposedChart,
	Legend,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';

/**
 * I quattro livelli energetici di un punto di misura, sovrapposti sullo stesso
 * asse temporale.
 *
 * Serve a vedere due cose insieme che nelle viste aggregate non si vedono: dove
 * ogni fonte copre davvero — il GSE arriva a conguaglio e lascia scoperto il
 * mese in corso, il misuratore ha buchi quando la telemetria cade — e quanto la
 * stima si discosta dalla misura reale quando entrambe ci sono.
 *
 * Il **tipico** è disegnato come area di sfondo e non come linea: copre sempre
 * tutti gli slot — è il livello che non lascia buchi — e come quinta linea piena
 * sovrasterebbe le altre invece di fare da fondo. Come banda si legge per quello
 * che è, un riferimento e non una misura di quel giorno.
 */
export interface PodLayerPoint {
	/** Etichetta dello slot, già formattata (es. '14:30') */
	slot: string;
	/** Livello 1 — certificato GSE */
	gse?: number | null;
	/** Livello 2 — rilevato dal contatore, o ricostruito da bolletta */
	reale?: number | null;
	/** Livello 3 — la curva tipica riscalata sui valori reali vicini */
	stimato?: number | null;
	/** Livello 4 — la sola curva tipica */
	tipico?: number | null;
	/**
	 * L'esito della riconciliazione slot per slot: il valore che il KPI ha usato.
	 *
	 * Non è un livello ma la loro risultante, e va disegnato perché è l'unico modo
	 * di vedere *dove* la priorità ha cambiato sorgente a metà giornata: senza
	 * questa linea si vedono quattro curve e non si sa quale abbia vinto dove.
	 */
	riconciliato?: number | null;
}

export interface PodLayersChartProps {
	data: PodLayerPoint[];
	/** Unità dell'asse y, default 'kWh' */
	unit?: string;
	height?: number;
	/** Nasconde la legenda quando il contenitore ne fornisce già una */
	hideLegend?: boolean;
}

// I colori sono presentazione e restano qui; i nomi e l'ordine dei livelli
// arrivano da @slims/shared/energy, che è l'unico posto che li dichiara.
const COLORS = {
	gse: '#1D9E75',
	reale: '#378ADD',
	stimato: '#EF9F27',
	tipico: '#9AA0A6',
	// `currentColor` e non un esadecimale: la risultante deve restare leggibile
	// sia su tema chiaro sia su scuro, dove un quasi-nero sparirebbe.
	riconciliato: 'currentColor',
} as const;

const LABELS = {
	gse: `${LAYER_LABEL.gse} (certificato)`,
	reale: `${LAYER_LABEL.reale} (rilevato o bolletta)`,
	stimato: LAYER_LABEL.stimato,
	tipico: `${LAYER_LABEL.tipico} (baseline)`,
	riconciliato: 'Riconciliato (usato dal KPI)',
} as const;

/** `true` se almeno uno slot porta un valore per quella serie. */
const has = (data: PodLayerPoint[], key: keyof PodLayerPoint) => data.some((d) => d[key] != null);

export function PodLayersChart({
	data,
	unit = 'kWh',
	height = 320,
	hideLegend,
}: PodLayersChartProps) {
	const showGse = has(data, 'gse');
	const showReale = has(data, 'reale');
	const showStimato = has(data, 'stimato');
	const showTipico = has(data, 'tipico');
	const showReconciled = has(data, 'riconciliato');

	return (
		<ResponsiveContainer width="100%" height={height}>
			<ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
				<CartesianGrid
					strokeDasharray="3 3"
					stroke="currentColor"
					opacity={0.12}
					vertical={false}
				/>
				<XAxis dataKey="slot" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={28} />
				<YAxis
					tick={{ fontSize: 11 }}
					width={52}
					label={{ value: unit, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
				/>
				<Tooltip
					formatter={(value, name) => [
						typeof value === 'number' ? `${value.toFixed(3)} ${unit}` : '-',
						String(name),
					]}
					labelFormatter={(l) => `Slot ${l}`}
					contentStyle={{ fontSize: 12 }}
				/>
				{!hideLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}

				{/* Fondo come area: è un riferimento, non una misura di quel giorno. */}
				{showTipico && (
					<Area
						type="monotone"
						dataKey="tipico"
						name={LABELS.tipico}
						stroke={COLORS.tipico}
						strokeDasharray="4 3"
						strokeWidth={1}
						fill={COLORS.tipico}
						fillOpacity={0.14}
						connectNulls
						isAnimationActive={false}
					/>
				)}
				{showStimato && (
					<Line
						type="monotone"
						dataKey="stimato"
						name={LABELS.stimato}
						stroke={COLORS.stimato}
						strokeWidth={1.5}
						strokeDasharray="5 4"
						dot={false}
						connectNulls={false}
						isAnimationActive={false}
					/>
				)}
				{showReale && (
					<Line
						type="monotone"
						dataKey="reale"
						name={LABELS.reale}
						stroke={COLORS.reale}
						strokeWidth={2}
						dot={false}
						// Senza connectNulls i buchi di telemetria restano visibili: è
						// un'informazione, non un difetto del grafico.
						connectNulls={false}
						isAnimationActive={false}
					/>
				)}
				{showReconciled && (
					<Line
						type="monotone"
						dataKey="riconciliato"
						name={LABELS.riconciliato}
						stroke={COLORS.riconciliato}
						strokeWidth={1}
						strokeOpacity={0.75}
						dot={false}
						connectNulls={false}
						isAnimationActive={false}
					/>
				)}
				{showGse && (
					<Line
						type="monotone"
						dataKey="gse"
						name={LABELS.gse}
						stroke={COLORS.gse}
						strokeWidth={2.5}
						dot={false}
						connectNulls={false}
						isAnimationActive={false}
					/>
				)}
			</ComposedChart>
		</ResponsiveContainer>
	);
}

export { COLORS as PodLayerColors, LABELS as PodLayerLabels };
